import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { serializeStructuredJsonPayload } from '../../shared/design-system/structured-json-form.utils';
import {
  CalculateInsuranceCessionsPayload,
  CreateInsuranceReinsuranceBordereauPayload,
  CreateInsuranceReinsuranceTreatyPayload,
  InsuranceApiService,
  InsuranceCession,
  InsuranceProductDetail,
  InsuranceProductListItem,
  InsuranceReinsuranceBordereau,
  InsuranceReinsuranceBordereauExport,
  InsuranceReinsuranceProgram,
  InsuranceReinsuranceTreatyDetail,
  PutInsuranceReinsuranceProgramsPayload,
  UpdateInsuranceReinsuranceTreatyPayload
} from './insurance-api.service';
import {
  REINSURANCE_PROGRAM_LINE_OF_BUSINESS_OPTIONS,
  REINSURANCE_TREATY_LAYER_FIELDS,
  REINSURANCE_TREATY_STATUS_OPTIONS,
  REINSURANCE_TREATY_TYPE_OPTIONS,
  emptyTreatyLayerItem,
  parseTreatyLayersJson,
  serializeTreatyLayersJson,
  treatyLayerItemFromPayload,
  treatyLayerSummary,
  treatyStatusLabelKey,
  treatyTypeLabelKey
} from './insurance-reinsurance-schemas';

type ReinsuranceTab = 'summary' | 'programs' | 'cessions' | 'bordereaux';

@Component({
  standalone: true,
  selector: 'ain-insurance-reinsurance-treaty-detail',
  imports: [
    AinAmountPipe,
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TableModule,
    DialogModule,
    PartySelectorComponent,
    JalaliDateInputComponent,
    ButtonModule,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.reinsurance.treatyDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.reinsurance.help.detail.title'"
        [descriptionKey]="'insurance.reinsurance.help.detail.description'"
        [bulletKeys]="[
          'insurance.reinsurance.help.detail.bullets.summary',
          'insurance.reinsurance.help.detail.bullets.programs',
          'insurance.reinsurance.help.detail.bullets.cessions'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/reinsurance/treaties']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.reinsurance.sections.create') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.reinsurance.help.create.title'"
              [descriptionKey]="'insurance.reinsurance.help.create.description'"
              [bulletKeys]="[
                'insurance.reinsurance.help.create.bullets.legalEntity',
                'insurance.reinsurance.help.create.bullets.reinsurer',
                'insurance.reinsurance.help.create.bullets.layers'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label><span>{{ t('insurance.reinsurance.treatyDetail.fields.legalEntityId') }}</span><ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" /></label>
            <label><span>{{ t('insurance.reinsurance.treaties.fields.code') }}</span><input pInputText [(ngModel)]="createForm.code" name="code" /></label>
            <label><span>{{ t('insurance.reinsurance.treaties.fields.name') }}</span><input pInputText [(ngModel)]="createForm.name" name="name" /></label>
            <label>
              <span>{{ t('insurance.reinsurance.treaties.fields.type') }}</span>
              <p-select
                [options]="treatyTypeOptions"
                optionValue="value"
                [(ngModel)]="createForm.treatyType"
                name="treatyType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label><span>{{ t('insurance.reinsurance.treatyDetail.fields.reinsurerPartyId') }}</span><ain-party-selector [(ngModel)]="createForm.reinsurerPartyId" name="reinsurerPartyId" /></label>
            <label><span>{{ t('insurance.reinsurance.treatyDetail.fields.cessionPercent') }}</span><p-inputNumber [(ngModel)]="createForm.cessionPercent" name="cessionPercent" [min]="0" [max]="1" [minFractionDigits]="0" [maxFractionDigits]="6" /></label>
            <label><span>{{ t('insurance.reinsurance.treatyDetail.fields.retentionLimit') }}</span><p-inputNumber [(ngModel)]="createForm.retentionLimit" name="retentionLimit" [min]="0" /></label>
            <label><span>{{ t('insurance.reinsurance.treaties.fields.effectiveFrom') }}</span><ain-date-input [(ngModel)]="createForm.effectiveFrom" name="effectiveFrom" /></label>
            <label><span>{{ t('insurance.reinsurance.treaties.fields.effectiveTo') }}</span><ain-date-input [(ngModel)]="createForm.effectiveTo" name="effectiveTo" /></label>
            <div class="wide">
              <div class="lines-header">
                <span class="field-label">{{ t('insurance.reinsurance.treatyDetail.fields.layersJson') }}</span>
                <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="openLayerCreate()"></button>
              </div>
              @if (layers().length === 0) {
                <p class="field-hint">{{ t('insurance.reinsurance.treatyDetail.layers.empty') }}</p>
              } @else {
                <p-table [value]="layers()" styleClass="lines-table">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('insurance.reinsurance.schemas.layer.summary') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                    <tr>
                      <td>{{ treatyLayerSummary(row) }}</td>
                      <td class="actions-cell">
                        <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openLayerEdit(rowIndex)"></button>
                        <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeLayer(rowIndex)"></button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </div>
          </form>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      } @else if (!item()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/reinsurance/treaties']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveUpdate()"></button>
            <button pButton type="button" severity="danger" [label]="t('common.delete')" [disabled]="saving()" (click)="deleteTreaty()"></button>
          </div>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.reinsurance.sections.summary') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.reinsurance.help.summary.title'"
              [descriptionKey]="'insurance.reinsurance.help.summary.description'"
              [bulletKeys]="[
                'insurance.reinsurance.help.summary.bullets.fields',
                'insurance.reinsurance.help.summary.bullets.dates',
                'insurance.reinsurance.help.summary.bullets.layers'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.reinsurance.treaties.fields.code') }}: <strong>{{ item()!.code }}</strong></span>
            <span>{{ t('insurance.reinsurance.treaties.fields.name') }}: <strong>{{ item()!.name }}</strong></span>
            <span>{{ t('insurance.reinsurance.treaties.fields.type') }}: <strong>{{ t(treatyTypeLabelKey(item()!.treatyType)) }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(treatyStatusLabelKey(item()!.status)) }}</strong></span>
            <span>{{ t('insurance.reinsurance.treaties.fields.effectiveFrom') }}: <strong>{{ item()!.effectiveFrom | ainDate }}</strong></span>
            <span>{{ t('insurance.reinsurance.treaties.fields.effectiveTo') }}: <strong>{{ item()!.effectiveTo | ainDate }}</strong></span>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.reinsurance.treatyDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'programs'" (click)="activeTab.set('programs')">{{ t('insurance.reinsurance.treatyDetail.tabs.programs') }}</button>
            <button type="button" [class.active]="activeTab() === 'cessions'" (click)="activeTab.set('cessions')">{{ t('insurance.reinsurance.treatyDetail.tabs.cessions') }}</button>
            <button type="button" [class.active]="activeTab() === 'bordereaux'" (click)="activeTab.set('bordereaux')">{{ t('insurance.reinsurance.treatyDetail.tabs.bordereaux') }}</button>
          </div>

          @if (activeTab() === 'summary') {
            <div class="lines-header">
              <h4>{{ t('insurance.reinsurance.treatyDetail.tabs.summary') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.reinsurance.help.summary.title'"
                [descriptionKey]="'insurance.reinsurance.help.summary.description'"
                [bulletKeys]="[
                  'insurance.reinsurance.help.summary.bullets.fields',
                  'insurance.reinsurance.help.summary.bullets.dates'
                ]" />
            </div>
            <form class="grid section-gap">
              <label><span>{{ t('insurance.reinsurance.treaties.fields.name') }}</span><input pInputText [(ngModel)]="updateForm.name" name="name" /></label>
              <label>
                <span>{{ t('insurance.reinsurance.treaties.fields.type') }}</span>
                <p-select
                  [options]="treatyTypeOptions"
                  optionValue="value"
                  [(ngModel)]="updateForm.treatyType"
                  name="treatyType"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.reinsurance.treaties.fields.status') }}</span>
                <p-select
                  [options]="treatyStatusOptions"
                  optionValue="value"
                  [(ngModel)]="updateForm.status"
                  name="status"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label><span>{{ t('insurance.reinsurance.treatyDetail.fields.reinsurerPartyId') }}</span><ain-party-selector [(ngModel)]="updateForm.reinsurerPartyId" name="reinsurerPartyId" /></label>
              <label><span>{{ t('insurance.reinsurance.treatyDetail.fields.cessionPercent') }}</span><p-inputNumber [(ngModel)]="updateForm.cessionPercent" name="cessionPercent" [min]="0" [max]="1" [maxFractionDigits]="6" /></label>
              <label><span>{{ t('insurance.reinsurance.treatyDetail.fields.retentionLimit') }}</span><p-inputNumber [(ngModel)]="updateForm.retentionLimit" name="retentionLimit" [min]="0" /></label>
              <label><span>{{ t('insurance.reinsurance.treaties.fields.effectiveFrom') }}</span><ain-date-input [(ngModel)]="updateForm.effectiveFrom" name="effectiveFrom" /></label>
              <label><span>{{ t('insurance.reinsurance.treaties.fields.effectiveTo') }}</span><ain-date-input [(ngModel)]="updateForm.effectiveTo" name="effectiveTo" /></label>
              <div class="wide">
                <div class="lines-header">
                  <span class="field-label">{{ t('insurance.reinsurance.treatyDetail.fields.layersJson') }}</span>
                  <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="openLayerCreate()"></button>
                </div>
                @if (layers().length === 0) {
                  <p class="field-hint">{{ t('insurance.reinsurance.treatyDetail.layers.empty') }}</p>
                } @else {
                  <p-table [value]="layers()" styleClass="lines-table">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('insurance.reinsurance.schemas.layer.summary') }}</th>
                        <th></th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                      <tr>
                        <td>{{ treatyLayerSummary(row) }}</td>
                        <td class="actions-cell">
                          <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openLayerEdit(rowIndex)"></button>
                          <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeLayer(rowIndex)"></button>
                        </td>
                      </tr>
                    </ng-template>
                  </p-table>
                }
              </div>
            </form>
          } @else if (activeTab() === 'programs') {
            <div class="lines-header">
              <h4>{{ t('insurance.reinsurance.treatyDetail.programs.title') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.reinsurance.help.tabs.programs.title'"
                  [descriptionKey]="'insurance.reinsurance.help.tabs.programs.description'"
                  [bulletKeys]="[
                    'insurance.reinsurance.help.tabs.programs.bullets.scope',
                    'insurance.reinsurance.help.tabs.programs.bullets.product'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openProgramCreate()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" [disabled]="saving()" (click)="savePrograms()"></button>
              </div>
            </div>
            @if (programs().length === 0) {
              <p>{{ t('insurance.reinsurance.treatyDetail.programs.empty') }}</p>
            } @else {
              <p-table [value]="programs()" dataKey="id" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.reinsurance.treatyDetail.programs.fields.productVersionId') }}</th>
                    <th>{{ t('insurance.reinsurance.treatyDetail.programs.fields.lineOfBusiness') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                  <tr>
                    <td>{{ productVersionLabel(row.productVersionId) }}</td>
                    <td>{{ lineOfBusinessLabel(row.lineOfBusiness) }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openProgramEdit(rowIndex)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeProgram(rowIndex)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'cessions') {
            <div class="lines-header">
              <h4>{{ t('insurance.reinsurance.treatyDetail.cessions.title') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.reinsurance.help.tabs.cessions.title'"
                  [descriptionKey]="'insurance.reinsurance.help.tabs.cessions.description'"
                  [bulletKeys]="[
                    'insurance.reinsurance.help.tabs.cessions.bullets.period',
                    'insurance.reinsurance.help.tabs.cessions.bullets.calculate'
                  ]" />
                <label class="inline-field">
                  <span>{{ t('insurance.reinsurance.treatyDetail.fields.accountingPeriod') }}</span>
                  <input pInputText [(ngModel)]="calculationPeriod" />
                </label>
                <button pButton type="button" [label]="t('insurance.reinsurance.treatyDetail.actions.calculate')" [disabled]="saving()" (click)="calculateCessions()"></button>
              </div>
            </div>
            @if (cessions().length === 0) {
              <p>{{ t('insurance.reinsurance.treatyDetail.cessions.empty') }}</p>
            } @else {
              <p-table [value]="cessions()" dataKey="id">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.reinsurance.treatyDetail.fields.accountingPeriod') }}</th>
                    <th>{{ t('insurance.reinsurance.treatyDetail.cessions.fields.sourceEventType') }}</th>
                    <th>{{ t('insurance.reinsurance.treatyDetail.cessions.fields.cededPremium') }}</th>
                    <th>{{ t('insurance.reinsurance.treatyDetail.cessions.fields.cededLoss') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.accountingPeriod }}</td>
                    <td>{{ row.sourceEventType }}</td>
                    <td>{{ row.cededPremium | ainAmount }}</td>
                    <td>{{ row.cededLoss | ainAmount }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.reinsurance.treatyDetail.bordereaux.title') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.reinsurance.help.tabs.bordereaux.title'"
                  [descriptionKey]="'insurance.reinsurance.help.tabs.bordereaux.description'"
                  [bulletKeys]="[
                    'insurance.reinsurance.help.tabs.bordereaux.bullets.generate',
                    'insurance.reinsurance.help.tabs.bordereaux.bullets.export'
                  ]" />
                <label class="inline-field">
                  <span>{{ t('insurance.reinsurance.treatyDetail.fields.accountingPeriod') }}</span>
                  <input pInputText [(ngModel)]="bordereauPeriod" />
                </label>
                <button pButton type="button" [label]="t('insurance.reinsurance.treatyDetail.actions.generateBordereau')" [disabled]="saving()" (click)="createBordereau()"></button>
                <button pButton type="button" severity="secondary" [label]="t('insurance.reinsurance.treatyDetail.actions.exportBordereau')" [disabled]="saving() || bordereaux().length === 0" (click)="exportLatestBordereau()"></button>
              </div>
            </div>
            @if (bordereaux().length === 0) {
              <p>{{ t('insurance.reinsurance.treatyDetail.bordereaux.empty') }}</p>
            } @else {
              <p-table [value]="bordereaux()" dataKey="id">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.reinsurance.treatyDetail.fields.accountingPeriod') }}</th>
                    <th>{{ t('insurance.reinsurance.treaties.fields.status') }}</th>
                    <th>{{ t('insurance.group.masterPolicyDetail.bulk.fields.processedCount') }}</th>
                    <th>{{ t('insurance.reinsurance.treatyDetail.cessions.fields.cededPremium') }}</th>
                    <th>{{ t('insurance.reinsurance.treatyDetail.cessions.fields.cededLoss') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.period }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.itemCount }}</td>
                    <td>{{ row.totalCededPremium | ainAmount }}</td>
                    <td>{{ row.totalCededLoss | ainAmount }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @if (latestExport()) {
              <p class="field-hint">{{ t('insurance.reinsurance.treatyDetail.export.ready') }} — {{ latestExport()!.fileName }}</p>
            }
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
      }

      <p-dialog [header]="layerDialogTitle()" [modal]="true" [(visible)]="layerDialogVisible" [style]="{ width: '640px' }">
        <ain-structured-json-form
          [fields]="layerFields"
          [(payloadJson)]="layerDraftJson" />
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="layerDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveLayerDialog()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="programDialogTitle()" [modal]="true" [(visible)]="programDialogVisible" [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label>
            <span>{{ t('insurance.reinsurance.treatyDetail.programs.fields.productVersionId') }}</span>
            <p-select
              [options]="productVersionOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="programDraft.productVersionId"
              name="programProductVersionId"
              [filter]="true"
              [showClear]="true"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('insurance.reinsurance.treatyDetail.programs.fields.lineOfBusiness') }}</span>
            <p-select
              [options]="lineOfBusinessOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="programDraft.lineOfBusiness"
              name="programLineOfBusiness"
              [showClear]="true"
              appendTo="body" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="programDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveProgramDialog()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .inline-field { display: grid; gap: 4px; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .wide { grid-column: 1 / -1; }
    .field-label { color: #475569; font-size: 12px; font-weight: 600; }
    .field-hint { font-size: 12px; color: #64748b; margin-top: 8px; }
    .actions-cell { display: flex; gap: 4px; justify-content: flex-end; white-space: nowrap; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceReinsuranceTreatyDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly item = signal<InsuranceReinsuranceTreatyDetail | null>(null);
  readonly isCreateMode = signal(false);
  readonly activeTab = signal<ReinsuranceTab>('summary');
  readonly error = signal('');
  readonly programs = signal<InsuranceReinsuranceProgram[]>([]);
  readonly cessions = signal<InsuranceCession[]>([]);
  readonly bordereaux = signal<InsuranceReinsuranceBordereau[]>([]);
  readonly latestExport = signal<InsuranceReinsuranceBordereauExport | null>(null);
  readonly layers = signal<Record<string, unknown>[]>([]);
  readonly productVersionOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly layerDialogVisible = signal(false);
  readonly programDialogVisible = signal(false);
  readonly treatyTypeOptions = [...REINSURANCE_TREATY_TYPE_OPTIONS];
  readonly treatyStatusOptions = [...REINSURANCE_TREATY_STATUS_OPTIONS];
  readonly lineOfBusinessOptions = [...REINSURANCE_PROGRAM_LINE_OF_BUSINESS_OPTIONS];
  readonly layerFields = REINSURANCE_TREATY_LAYER_FIELDS;
  readonly treatyLayerSummary = treatyLayerSummary;
  readonly treatyTypeLabelKey = treatyTypeLabelKey;
  readonly treatyStatusLabelKey = treatyStatusLabelKey;
  calculationPeriod = new Date().toISOString().slice(0, 7);
  bordereauPeriod = new Date().toISOString().slice(0, 7);
  layerDraftJson = serializeStructuredJsonPayload(emptyTreatyLayerItem());
  programDraft: { productVersionId: string | null; lineOfBusiness: string | null } = {
    productVersionId: null,
    lineOfBusiness: null
  };
  private layerDialogMode: 'create' | 'edit' = 'create';
  private layerDialogIndex = -1;
  private programDialogMode: 'create' | 'edit' = 'create';
  private programDialogIndex = -1;
  private productVersionIndex = new Map<string, string>();

  readonly pageTitle = computed(() =>
    this.isCreateMode()
      ? this.t('insurance.reinsurance.treaties.actions.create')
      : this.item()
        ? `${this.item()!.code} — ${this.item()!.name}`
        : this.t('insurance.reinsurance.treatyDetail.title'));

  createForm: CreateInsuranceReinsuranceTreatyPayload = {
    legalEntityId: '',
    code: '',
    name: '',
    treatyType: 'QuotaShare',
    reinsurerPartyId: '',
    effectiveFrom: new Date().toISOString(),
    effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    cessionPercent: 0.5,
    retentionLimit: 0,
    layersJson: '[]'
  };

  updateForm: UpdateInsuranceReinsuranceTreatyPayload = {
    name: '',
    treatyType: 'QuotaShare',
    reinsurerPartyId: '',
    effectiveFrom: new Date().toISOString(),
    effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    cessionPercent: 0.5,
    retentionLimit: 0,
    layersJson: '[]',
    status: 'Active'
  };

  ngOnInit(): void {
    this.loadProductVersionOptions();
    const isNew = this.route.snapshot.url.at(-1)?.path === 'new';
    if (isNew) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    this.load();
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('treatyId');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.api.getReinsuranceTreaty(id).subscribe({
      next: treaty => {
        this.item.set(treaty);
        if (treaty) {
          this.updateForm = {
            name: treaty.name,
            treatyType: treaty.treatyType,
            reinsurerPartyId: treaty.reinsurerPartyId,
            effectiveFrom: treaty.effectiveFrom,
            effectiveTo: treaty.effectiveTo,
            cessionPercent: treaty.cessionPercent,
            retentionLimit: treaty.retentionLimit,
            layersJson: treaty.layersJson,
            status: treaty.status
          };
          this.layers.set(parseTreatyLayersJson(treaty.layersJson));
          this.programs.set(treaty.programs.map(item => ({ ...item })));
          this.loadCessionsAndBordereaux(treaty.id);
        }
        this.loading.set(false);
      },
      error: () => {
        this.item.set(null);
        this.loading.set(false);
      }
    });
  }

  private loadProductVersionOptions(): void {
    this.api.listProducts().subscribe({
      next: products => this.buildProductVersionOptions(products),
      error: () => {
        this.productVersionOptions.set([]);
        this.productVersionIndex.clear();
      }
    });
  }

  private buildProductVersionOptions(products: InsuranceProductListItem[]): void {
    if (products.length === 0) {
      this.productVersionOptions.set([]);
      this.productVersionIndex.clear();
      return;
    }

    let pending = products.length;
    const options: Array<{ label: string; value: string }> = [];
    const index = new Map<string, string>();

    products.forEach(product => {
      this.api.getProduct(product.id).subscribe({
        next: detail => this.indexProductVersions(product, detail, options, index),
        error: () => {},
        complete: () => {
          pending -= 1;
          if (pending === 0) {
            options.sort((a, b) => a.label.localeCompare(b.label));
            this.productVersionOptions.set(options);
            this.productVersionIndex = index;
          }
        }
      });
    });
  }

  private indexProductVersions(
    product: InsuranceProductListItem,
    detail: InsuranceProductDetail | null,
    options: Array<{ label: string; value: string }>,
    index: Map<string, string>
  ): void {
    (detail?.versions ?? []).forEach(version => {
      const label = `${product.code} v${version.versionNumber} (${version.status})`;
      options.push({ value: version.id, label });
      index.set(version.id, label);
    });
  }

  productVersionLabel(productVersionId: string | null): string {
    if (!productVersionId) {
      return '-';
    }
    return this.productVersionIndex.get(productVersionId) ?? productVersionId;
  }

  lineOfBusinessLabel(value: string | null): string {
    if (!value) {
      return '-';
    }
    return this.lineOfBusinessOptions.find(item => item.value === value)?.label ?? value;
  }

  layerDialogTitle(): string {
    return this.layerDialogMode === 'create'
      ? this.t('insurance.reinsurance.treatyDetail.layers.add')
      : this.t('insurance.reinsurance.treatyDetail.layers.edit');
  }

  programDialogTitle(): string {
    return this.programDialogMode === 'create'
      ? this.t('insurance.reinsurance.treatyDetail.programs.add')
      : this.t('insurance.reinsurance.treatyDetail.programs.edit');
  }

  openLayerCreate(): void {
    this.layerDialogMode = 'create';
    this.layerDialogIndex = -1;
    this.layerDraftJson = serializeStructuredJsonPayload(emptyTreatyLayerItem());
    this.layerDialogVisible.set(true);
  }

  openLayerEdit(index: number): void {
    const row = this.layers()[index];
    if (!row) {
      return;
    }
    this.layerDialogMode = 'edit';
    this.layerDialogIndex = index;
    this.layerDraftJson = serializeStructuredJsonPayload(treatyLayerItemFromPayload(JSON.stringify(row)));
    this.layerDialogVisible.set(true);
  }

  saveLayerDialog(): void {
    const item = treatyLayerItemFromPayload(this.layerDraftJson);
    const next = [...this.layers()];
    if (this.layerDialogMode === 'edit' && this.layerDialogIndex >= 0) {
      next[this.layerDialogIndex] = item;
    } else {
      next.push(item);
    }
    this.layers.set(next);
    this.layerDialogVisible.set(false);
  }

  removeLayer(index: number): void {
    this.layers.set(this.layers().filter((_, itemIndex) => itemIndex !== index));
  }

  openProgramCreate(): void {
    this.programDialogMode = 'create';
    this.programDialogIndex = -1;
    this.programDraft = { productVersionId: null, lineOfBusiness: null };
    this.programDialogVisible.set(true);
  }

  openProgramEdit(index: number): void {
    const row = this.programs()[index];
    if (!row) {
      return;
    }
    this.programDialogMode = 'edit';
    this.programDialogIndex = index;
    this.programDraft = {
      productVersionId: row.productVersionId,
      lineOfBusiness: row.lineOfBusiness
    };
    this.programDialogVisible.set(true);
  }

  saveProgramDialog(): void {
    const treatyId = this.item()?.id ?? '';
    const nextRow: InsuranceReinsuranceProgram = {
      id: this.programDialogMode === 'edit' ? this.programs()[this.programDialogIndex]?.id ?? '' : '',
      treatyId,
      productVersionId: this.programDraft.productVersionId || null,
      lineOfBusiness: this.programDraft.lineOfBusiness || null
    };
    const next = [...this.programs()];
    if (this.programDialogMode === 'edit' && this.programDialogIndex >= 0) {
      next[this.programDialogIndex] = nextRow;
    } else {
      next.push(nextRow);
    }
    this.programs.set(next);
    this.programDialogVisible.set(false);
  }

  removeProgram(index: number): void {
    this.programs.set(this.programs().filter((_, itemIndex) => itemIndex !== index));
  }

  private loadCessionsAndBordereaux(treatyId: string): void {
    this.api.listReinsuranceCessions({ treatyId, accountingPeriod: this.calculationPeriod }).subscribe(rows => this.cessions.set(rows));
    this.api.listReinsuranceBordereaux({ treatyId }).subscribe(rows => this.bordereaux.set(rows));
  }

  private currentLayersJson(): string {
    return serializeTreatyLayersJson(this.layers());
  }

  saveCreate(): void {
    this.saving.set(true);
    this.error.set('');
    this.createForm.layersJson = this.currentLayersJson();
    this.api.createReinsuranceTreaty(this.createForm).subscribe({
      next: treaty => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/reinsurance/treaties', treaty.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveUpdate(): void {
    const treaty = this.item();
    if (!treaty) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.updateForm.layersJson = this.currentLayersJson();
    this.api.updateReinsuranceTreaty(treaty.id, this.updateForm).subscribe({
      next: updated => {
        this.item.set(updated);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  deleteTreaty(): void {
    const treaty = this.item();
    if (!treaty || !confirm(this.t('insurance.reinsurance.treatyDetail.messages.deleteConfirm'))) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.api.deleteReinsuranceTreaty(treaty.id).subscribe({
      next: () => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/reinsurance/treaties']);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  savePrograms(): void {
    const treaty = this.item();
    if (!treaty) {
      return;
    }
    const payload: PutInsuranceReinsuranceProgramsPayload = {
      items: this.programs().map(item => ({
        productVersionId: item.productVersionId || null,
        lineOfBusiness: item.lineOfBusiness || null
      }))
    };
    this.saving.set(true);
    this.error.set('');
    this.api.putReinsurancePrograms(treaty.id, payload).subscribe({
      next: rows => {
        this.programs.set(rows);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  calculateCessions(): void {
    const treaty = this.item();
    if (!treaty || !confirm(this.t('insurance.reinsurance.treatyDetail.messages.calculateConfirm'))) {
      return;
    }

    const payload: CalculateInsuranceCessionsPayload = {
      accountingPeriod: this.calculationPeriod,
      treatyId: treaty.id,
      policyId: null,
      claimId: null
    };
    this.saving.set(true);
    this.error.set('');
    this.api.calculateReinsuranceCessions(payload).subscribe({
      next: result => {
        this.cessions.set(result.cessions);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  createBordereau(): void {
    const treaty = this.item();
    if (!treaty || !confirm(this.t('insurance.reinsurance.treatyDetail.messages.generateBordereauConfirm'))) {
      return;
    }

    const payload: CreateInsuranceReinsuranceBordereauPayload = {
      period: this.bordereauPeriod,
      treatyId: treaty.id
    };
    this.saving.set(true);
    this.error.set('');
    this.api.createReinsuranceBordereau(payload).subscribe({
      next: created => {
        this.bordereaux.set([created, ...this.bordereaux()]);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  exportLatestBordereau(): void {
    const latest = this.bordereaux()[0];
    if (!latest || !confirm(this.t('insurance.reinsurance.treatyDetail.messages.exportBordereauConfirm'))) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.api.exportReinsuranceBordereau(latest.id).subscribe({
      next: exportPayload => {
        this.latestExport.set(exportPayload);
        this.saving.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
